import cx from 'classnames';
import { Surface } from '@dx-ui/global-ui-prism';

import { useHeaderContext } from './context/header-context';
import { useHeaderStateContext } from './context/header-state-context';

import { MobileTopSection } from './composable/mobile/top-section';
import { MobileBottomSection } from './composable/mobile/bottom-section';
import { MobileMenuDrawerTrigger } from './composable/mobile/menu-drawer-trigger';
import { DrawerOverlay } from './composable/mobile/drawer-overlay';
import { MobileMenuDrawer } from './composable/mobile/menu-drawer';
import { MobileAccountSidebar } from './composable/mobile/account-sidebar';

import { MobileAccountSidebarItems } from './composable/mobile/account-sidebar-items';
import { LanguageSelector } from '@dx-ui/osc-language-selector';
import { MobileBottomFindYourStayLink } from './composable/mobile/bottom-find-your-stay-link';
import { HeaderLogoLink } from './header.logo';
import { MobileAuthCluster } from './composable/mobile/auth-cluster';
import { MobileNavDrawerList } from './composable/mobile/nav-drawer-list';
import { DesktopContainer } from './composable/desktop/container';
import { DesktopTopSection } from './composable/desktop/top-section';
import { UserNavContainer } from './composable/desktop/user-nav/user-nav';
import { SignedInUserMenu } from './composable/desktop/user-nav/signedin-user-menu';
import { SignedOutAuthCluster } from './composable/desktop/user-nav/signed-out-auth-cluster';
import { FindStayNavigationLink } from './composable/desktop/user-nav/find-stay-navigation-link';
import { DesktopBottomSection } from './composable/desktop/bottom-section';
import { MobileContainer } from './composable/mobile/container';
import { DesktopHamburgerNavTrigger } from './composable/desktop/hamburger-nav-trigger';
import { DesktopMainNav } from './composable/desktop/main-nav';
import { DesktopLogo } from './composable/desktop/logo';
import { HEADER_VARIANT_TYPE } from './header.constants';

export const HamburgerHeaderVariant = ({ surfaceClassName }: { surfaceClassName?: string }) => {
  const {
    brand,
    user: { user },
    visual: { surfaceType, isTranslucent, suppressLogo, heading, isReimagineThemeExperience },
    navigation: { menuItems, languageSelectorOptions, headerVariantType },
  } = useHeaderContext();
  const {
    menuState: { isAccountOpen },
    displayFlags: { shouldShowHamburger },
    styling: { logoVariant },
  } = useHeaderStateContext();

  return (
    <Surface
      type={surfaceType || 'base'}
      className={cx('osc-header-wrapper', surfaceClassName, {
        '!bg-translucent': isTranslucent && !isReimagineThemeExperience,
        '!bg-transparent': isTranslucent && isReimagineThemeExperience,
      })}
    >
      <MobileContainer>
        <MobileTopSection>
          <MobileMenuDrawerTrigger />
          {languageSelectorOptions ? (
            <div className="pe-1 pt-2 md:pe-7">
              <LanguageSelector {...languageSelectorOptions} />
            </div>
          ) : null}
        </MobileTopSection>

        <MobileBottomSection
          leadingChildClassname="flex"
          leadingChild={
            !suppressLogo ? (
              <HeaderLogoLink
                variant={logoVariant}
                brand={brand}
                className={cx({
                  'self-end sm:self-center': !!languageSelectorOptions,
                })}
                heading={heading}
              />
            ) : null
          }
          trailingChild={
            <>
              <MobileBottomFindYourStayLink />
              <MobileAuthCluster />
            </>
          }
        />
        <DrawerOverlay />
        <MobileMenuDrawer className="left-0">
          <MobileNavDrawerList />
        </MobileMenuDrawer>
        <MobileAccountSidebar>
          {user && isAccountOpen ? <MobileAccountSidebarItems /> : null}
        </MobileAccountSidebar>
      </MobileContainer>

      <DesktopContainer>
        {headerVariantType === HEADER_VARIANT_TYPE.hamburgerNavMinimalReimagine ? (
          <DesktopTopSection
            leadingChild={
              <div className="flex min-h-[4.5rem] items-center px-2">
                <HeaderLogoLink variant={logoVariant} brand={brand} />
              </div>
            }
            trailingChildClassname="flex items-center justify-end"
            trailingChild={
              <>
                <UserNavContainer className="relative">
                  <FindStayNavigationLink />
                  {user ? <SignedInUserMenu /> : <SignedOutAuthCluster />}
                </UserNavContainer>
                <DesktopHamburgerNavTrigger />
              </>
            }
          />
        ) : (
          <DesktopTopSection
            leadingChild={<DesktopHamburgerNavTrigger />}
            trailingChild={
              <UserNavContainer>
                <FindStayNavigationLink />
                {user ? <SignedInUserMenu /> : <SignedOutAuthCluster />}
              </UserNavContainer>
            }
          />
        )}

        {!!menuItems?.length &&
          shouldShowHamburger &&
          headerVariantType !== HEADER_VARIANT_TYPE.hamburgerNavMinimal &&
          headerVariantType !== HEADER_VARIANT_TYPE.hamburgerNavMinimalReimagine && (
            <DesktopBottomSection>
              <DesktopMainNav
                mainNavLinks={menuItems}
                leadingChild={!suppressLogo ? <DesktopLogo className="pe-4 ps-1" /> : null}
              />
            </DesktopBottomSection>
          )}
      </DesktopContainer>
    </Surface>
  );
};
